import { useCallback, useState } from 'react'
import { Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useDeleteProductDrawing,
  useGetProductDrawings,
  useUploadProductDrawing,
} from '@/shared/hooks/api/rohsProductDrawings'
import { useCanWriteOrgData } from '@/shared/hooks/useCanWriteOrgData'
import { AddProductDrawingsDialog } from '@/v2/components/AddProductDrawingsDialog'
import { ProductDrawingListItem } from '@/v2/components/ProductDrawingListItem'
import { V2ProductDrawingsGuidance } from '@/v2/components/V2ProductDrawingsGuidance'
import type { RohsBriefingStep } from '../../utils/rohsLegislationBriefing'
import { V2RohsLegislationBriefingStepShell } from './V2RohsLegislationBriefingStepShell'

export function V2RohsProductDrawingsStep({
  step,
  index,
  productId,
}: {
  step: RohsBriefingStep
  index: number
  productId: string
}) {
  const { t } = useTranslation('common')
  const canWrite = useCanWriteOrgData()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [uploadingFileNames, setUploadingFileNames] = useState<Set<string>>(
    () => new Set()
  )

  const { data, isLoading } = useGetProductDrawings(productId)
  const { mutateAsync: uploadDrawing } = useUploadProductDrawing(productId)
  const { mutateAsync: deleteDrawing, isPending: isDeletePending } =
    useDeleteProductDrawing(productId)

  const handleUploadFiles = useCallback(
    async (files: File[]) => {
      for (const file of files) {
        setUploadingFileNames((current) => new Set(current).add(file.name))
        try {
          await uploadDrawing(file)
        } catch {
          toast.error(
            t('v2.pages.products.detail.drawings.actions.uploadFailed', {
              fileName: file.name,
            })
          )
        } finally {
          setUploadingFileNames((current) => {
            const next = new Set(current)
            next.delete(file.name)
            return next
          })
        }
      }
    },
    [t, uploadDrawing]
  )

  const handleDelete = useCallback(
    async (attachmentId: string) => {
      try {
        await deleteDrawing(attachmentId)
      } catch {
        toast.error(t('v2.pages.products.detail.drawings.actions.deleteFailed'))
      }
    },
    [deleteDrawing, t]
  )

  const files = data?.files ?? []
  const hasFiles = files.length > 0

  let actionLabel = t('v2.pages.products.detail.sharedEvidence.actions.upload')
  if (hasFiles) {
    actionLabel = t('v2.pages.products.detail.drawings.addButton')
  }

  return (
    <>
      <V2RohsLegislationBriefingStepShell
        step={step}
        index={index}
        actions={
          canWrite ? (
            <Button
              type='button'
              variant='outline'
              size='sm'
              className='gap-1'
              onClick={() => {
                setDialogOpen(true)
              }}
            >
              {hasFiles ? <Plus className='size-4' /> : null}
              {actionLabel}
            </Button>
          ) : undefined
        }
      >
        <V2ProductDrawingsGuidance />
        {isLoading ? <Skeleton className='h-10 w-full' /> : undefined}
        {hasFiles ? (
          <ul className='space-y-2'>
            {files.map((file) => (
              <li key={file.id}>
                <ProductDrawingListItem
                  file={file}
                  isUploading={uploadingFileNames.has(file.fileName)}
                  deleteDisabled={!canWrite || isDeletePending}
                  onDelete={() => handleDelete(file.id)}
                />
              </li>
            ))}
          </ul>
        ) : undefined}
      </V2RohsLegislationBriefingStepShell>
      {canWrite ? (
        <AddProductDrawingsDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onUploadFiles={handleUploadFiles}
        />
      ) : null}
    </>
  )
}
